import React, {CSSProperties, ForwardedRef, forwardRef, Ref, useState} from "react";
import CodeMirror, {ReactCodeMirrorRef} from "@uiw/react-codemirror";
import {vscodeDark} from "@uiw/codemirror-theme-vscode";
import {InputBaseComponentProps, TextField} from "@mui/material";
import {GlobalState} from "../../../global/GlobalState";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {Extension} from "@codemirror/state";
import {EditorView} from "@codemirror/view";
import {Diagnostic} from "@codemirror/lint";

export interface RawCodeTextFieldProps {
	readonly inputRef?: Ref<ReactCodeMirrorRef>;
	readonly value: string;
	readonly onChange?: (value: string, event: any) => void;
	readonly disabled?: boolean;
	readonly syntax: Extension;
	readonly formatter?: (value: string) => string;
	readonly maxHeight?: string;
}

export function RawCodeTextField(props: RawCodeTextFieldProps & InputBaseComponentProps) {
	const [code, setCode] = useState<string>(props.value);
	const [previousValue, setPreviousValue] = useState<string>(props.value);
	
	// determines the reason for a change
	// externalChange means that the outer layer's value changed and the "code" state has been reset, this change should not be forwarded on the onChange listener
	// feedbackChange means that the outer layer's value is expecting to be changed due to feedback of the onChange being invoked, requiring the "previousValue" state to be reset
	// default means that there is no outer change nor feedback change, changes made are done by the user
	const [changeState, setChangeState] = useState<"default" | "feedbackChange" | "externalChange">("default");
	if (changeState === "feedbackChange") {
		setPreviousValue(props.value);
		setChangeState("default");
	} else if (previousValue !== props.value) {
		setCode(props.value);
		setPreviousValue(props.value);
		setChangeState("externalChange");
	}
	
	return <div style={{ overflow: "auto", width: "100%" }}>
		<CodeMirror
			value={code}
			maxHeight={props.maxHeight ?? "400px"}
			placeholder={props.placeholder}
			extensions={[props.syntax]}
			onChange={(value, event) => {
				setCode(value);
				if (props.onChange && changeState !== "externalChange") {
					props.onChange(value, event);
				}
				setChangeState("feedbackChange");
			}}
			onKeyDown={event => {
				if (event.key === "F" && event.altKey && event.shiftKey) {
					if (props.formatter) {
						try {
							const newCode = props.formatter(code);
							setCode(newCode);
						} catch (ex) {
							// ignored
						}
					}
				}
			}}
			theme={GlobalState.paletteMode === "dark" ? vscodeDark : undefined}
			style={{flexGrow: 1}}
			editable={!props.disabled}
			indentWithTab={false}
			onBlur={() => {
				if (props.formatter) {
					try {
						const newCode = props.formatter(code);
						// using timeout to fix focus
						window.setTimeout(() => {
							const active: any = document.activeElement;
							setCode(newCode);
							window.setTimeout(() => active?.focus(), 0);
						}, 0);
					} catch (ex) {
						// ignored
					}
				}
			}}
		/>
	</div>;
}

const CodeInputComponent = forwardRef((props: any, ref: ForwardedRef<any>) => (
	<RawCodeTextField
		inputRef={ref}
		{...props}
		onChange={(value, event) => {
			if (props.onChange)
				props.onChange({...new Event("input", {}), value: value, event: event} as any);
		}}
	/>
));

export interface CodeTextFieldProps {
	readonly syntax: Extension;
	readonly linter?: (view: EditorView) => Diagnostic[];
	readonly formatter?: (value: string) => string;
	readonly TextFieldProps?: TextFieldProps;
	readonly maxHeight?: string;
	readonly disabled?: string;
	readonly style?: CSSProperties;
}

/**
 * MUI styled code editor
 */
export function CodeTextField(props: CodeTextFieldProps) {
	const [isError, setIsError] = useState(false);
	return <TextField
		{...props.TextFieldProps}
		color={"warning"}
		InputLabelProps={{
			...props.TextFieldProps?.InputLabelProps,
			shrink: true
		}}
		InputProps={{
			...props.TextFieldProps?.InputProps,
			inputProps: {
				...props.TextFieldProps?.InputProps?.inputProps,
				syntax: props.syntax,
				formatter: props.formatter,
				maxHeight: props.maxHeight,
				style: props.style
			},
			inputComponent: CodeInputComponent
		}}
		onChange={(event: any) => {
			if (props.linter) {
				const errors = props.linter(event.event.view);
				setIsError(errors.length > 0);
			}
			if (props.TextFieldProps?.onChange)
				props.TextFieldProps?.onChange(event);
		}}
		multiline={true}
		error={isError}
	/>;
}
